import { MutableRefObject, useRef } from 'react';
import { FlagIcon } from 'react-flag-kit';
import { alpha, useTheme } from '@mui/material/styles';
import { MenuButton } from 'components/dropdown-menu';
import {
  AppLocale,
  getLocaleLanguage,
  initIntl,
  setCurrentLocale
} from 'modules/i18n';
import { logError } from 'utils/datadog';
import { mapFlagCode } from '../constants';

type Props = {
  label: string;
  locale: AppLocale;
  onSelect?: (locale: AppLocale) => Promise<void>;
  /**
   * Shared with the sibling `MenuItem` in the same `Switcher`, so a second click on the OTHER
   * option is refused too — not just a second click on this one. Optional and defaulted to a
   * private ref so a standalone `MenuItem` (as in this component's own tests) still works.
   */
  selectingRef?: MutableRefObject<boolean>;
};

export const MenuItem = ({ label, locale, onSelect, selectingRef }: Props) => {
  const theme = useTheme();
  const privateRef = useRef(false);
  const lockRef = selectingRef ?? privateRef;

  const appLocale = getLocaleLanguage();

  const getSxProps = () => ({
    '>p': locale === appLocale ? { fontWeight: 600 } : {},
    backgroundColor:
      locale === appLocale
        ? alpha(theme.palette.action.selected, 0.16)
        : 'transparent',
    '>img': { marginRight: 1 }
  });

  /**
   * `onClick` is fire-and-forget — nothing downstream ever awaits this function's own promise —
   * so a rejection anywhere in this chain would otherwise reach the browser as an unhandled
   * promise rejection rather than a reported, controlled failure. `onSelect` in particular is
   * meant to be able to reject (the guest form's own handler does, deliberately, to hold back
   * the reload on a failed save), so this has to catch rather than assume the chain always
   * succeeds.
   *
   * Swallowed here rather than surfaced in the UI: the caller already decided what a failure
   * here should look like by choosing whether `onSelect` rejects at all, and letting the reload
   * fall through on failure would undo that decision.
   *
   * The lock is claimed FIRST, synchronously, before `setCurrentLocale`/`initIntl` ever run —
   * not only around `onSelect`. The menu stays open across the `await initIntl(locale)` below,
   * so without this a second click on the OTHER item — while this one is still mid-flight — would
   * start its own independent chain: two concurrent `setCurrentLocale`/`initIntl` calls racing
   * each other, and whichever `onSelect` resolves last reloading over the other's.
   */
  const handleSelectLanguage = async (locale: string) => {
    if (lockRef.current) {
      return;
    }
    lockRef.current = true;
    try {
      setCurrentLocale(locale);
      await initIntl(locale);

      if (onSelect) {
        await onSelect(locale as AppLocale);
      }

      window.location.reload();
    } catch (error) {
      logError('Language switch failed', error, {
        feature: 'language-switcher',
        locale
      });
      // Released only on failure: a success is about to reload the page anyway, wiping this ref
      // along with everything else, whereas a failed switch has to stay usable for a retry.
      lockRef.current = false;
    }
  };

  return (
    <MenuButton
      label={label}
      color="text.primary"
      icon={
        <FlagIcon
          code={mapFlagCode[locale]}
          size={20}
          width={28}
          style={{ borderRadius: 3 }}
        />
      }
      onClick={() => handleSelectLanguage(locale)}
      sx={getSxProps}
    />
  );
};
